import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getTablePreference,
  saveTablePreference,
  type TablePreference,
} from '@/api/tablePreferences';
import { useApiMutation, useApiQuery } from '@/hooks/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { queryKeys } from '@/shared/lib/queryKeys';
import {
  createDefaultDataTableLayout,
  type DataTableColumn,
  type DataTableLayout,
} from '@/UI/DataTable/types';
import {
  dataTableLayoutsEqual,
  reconcileDataTableLayout,
} from '@/UI/DataTable/useDataTableLayout';

const SAVE_DELAY_MS = 400;

type UsePersistedDataTableLayoutOptions<T> = {
  /** Must match the API pattern `^[a-z0-9-]+$` — never interpolate display data. */
  tableId: string;
  schemaVersion: number;
  columns: DataTableColumn<T>[];
  defaultPageSize: number;
};

type ResolvedPersistedLayout = {
  layout: DataTableLayout;
  pageSize: number;
};

/**
 * Maps a saved preference (or null) onto the current column schema.
 * Pure — used by the hook and unit tests so restore logic is not effect-timed.
 */
export function resolvePersistedDataTableLayout<T>(
  preference: Pick<
    TablePreference,
    'order' | 'hidden' | 'widths' | 'pageSize'
  > | null,
  columns: DataTableColumn<T>[],
  defaultPageSize: number,
): ResolvedPersistedLayout {
  if (!preference) {
    return {
      layout: createDefaultDataTableLayout(columns),
      pageSize: defaultPageSize,
    };
  }
  return {
    layout: reconcileDataTableLayout({ ...preference, widths: {} }, columns),
    pageSize: preference.pageSize,
  };
}

export function usePersistedDataTableLayout<T>({
  tableId,
  schemaVersion,
  columns,
  defaultPageSize,
}: UsePersistedDataTableLayoutOptions<T>) {
  const { user } = useCurrentUser();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const defaultLayout = useMemo(
    () => createDefaultDataTableLayout(columns),
    [columns],
  );
  const [layout, setLayoutState] = useState(defaultLayout);
  const [pageSize, setPageSizeState] = useState(defaultPageSize);
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);
  const preferenceQuery = useApiQuery({
    queryKey: queryKeys.user.tablePreference(
      tableId,
      schemaVersion,
      userId ?? '',
    ),
    queryFn: () => getTablePreference(tableId, schemaVersion),
    enabled: Boolean(userId),
    staleTime: Infinity,
  });
  const { mutate: persist } = useApiMutation({
    mutationFn: saveTablePreference,
    onSuccess: (data) => {
      if (!userId) return;
      queryClient.setQueryData(
        queryKeys.user.tablePreference(tableId, schemaVersion, userId),
        data,
      );
    },
  });

  const querySettled =
    Boolean(userId) &&
    (preferenceQuery.isSuccess ||
      (preferenceQuery.isError && !preferenceQuery.isFetching));
  const hydrateSignature = querySettled
    ? `${userId}:${tableId}:${schemaVersion}:${preferenceQuery.isSuccess}:${preferenceQuery.dataUpdatedAt}`
    : null;
  const isHydrated =
    hydratedKey === hydrateSignature && hydrateSignature != null;

  // Apply saved preference during render (cache hit or after fetch) so the first
  // paint after settle matches order/hidden — not a post-paint effect.
  // Re-run when user/token identity or fetched data changes (embed session swap).
  if (hydrateSignature && hydratedKey !== hydrateSignature) {
    const resolved = preferenceQuery.isSuccess
      ? resolvePersistedDataTableLayout(
          preferenceQuery.data,
          columns,
          defaultPageSize,
        )
      : { layout: defaultLayout, pageSize: defaultPageSize };
    setLayoutState(resolved.layout);
    setPageSizeState(resolved.pageSize);
    setHydratedKey(hydrateSignature);
  }

  // Column schema can change after hydrate (role flags, feature flags, etc.).
  if (isHydrated) {
    const nextLayout = reconcileDataTableLayout(layout, columns);
    if (!dataTableLayoutsEqual(layout, nextLayout)) {
      setLayoutState(nextLayout);
    }
  }

  useEffect(() => {
    window.clearTimeout(saveTimer.current);
  }, [schemaVersion, tableId, userId]);

  useEffect(
    () => () => {
      window.clearTimeout(saveTimer.current);
    },
    [],
  );

  const scheduleSave = useCallback(
    (nextLayout: DataTableLayout, nextPageSize: number) => {
      if (!userId || hydratedKey == null) return;
      if (!hydratedKey.startsWith(`${userId}:${tableId}:${schemaVersion}:`)) {
        return;
      }
      window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        persist({
          tableId,
          schemaVersion,
          order: nextLayout.order,
          hidden: nextLayout.hidden,
          widths: {},
          pageSize: nextPageSize,
        });
      }, SAVE_DELAY_MS);
    },
    [hydratedKey, persist, schemaVersion, tableId, userId],
  );

  const setLayout = useCallback(
    (nextLayout: DataTableLayout) => {
      setLayoutState(nextLayout);
      const persistableUnchanged =
        JSON.stringify(nextLayout.order) === JSON.stringify(layout.order) &&
        JSON.stringify(nextLayout.hidden) === JSON.stringify(layout.hidden);
      if (persistableUnchanged) return;
      scheduleSave(nextLayout, pageSize);
    },
    [layout, pageSize, scheduleSave],
  );

  const setPageSize = useCallback(
    (nextPageSize: number) => {
      setPageSizeState(nextPageSize);
      scheduleSave(layout, nextPageSize);
    },
    [layout, scheduleSave],
  );

  return {
    layout,
    setLayout,
    pageSize,
    setPageSize,
    // Stay on skeleton until this user/table/schema has been hydrated once.
    // Prefer !hydrated over isLoading so cache hits do not flash defaults.
    preferencesLoading: !isHydrated,
    preferencesError: preferenceQuery.error,
  };
}
